import { useState } from 'react';

import { PurchaseNoticeModal } from './PurchaseNoticeModal';
import { useKiosk } from '../lib/session';

/** 服務鈴按下後的提示停留時間。 */
const ACK_MS = 3000;

export interface FooterStep {
  /** 目前第幾步（1 起算）。 */
  current: number;
  /** 總步數。 */
  total: number;
}

interface BrandFooterProps {
  /** 購票流程的步驟指示；未給則中央留白（Attract、報到等非線性畫面）。 */
  step?: FooterStep;
}

/**
 * 頁尾工具列 — 業主 2026-08-18 設計稿：客服協助 · 步驟指示 · 購票須知。
 *
 * 深藍底、上緣金色波浪，三個區塊在所有畫面固定存在（付款授權中除外，
 * 那個畫面不可中斷，見 {@link KioskFrame}）。
 */
export function BrandFooter({ step }: BrandFooterProps): JSX.Element {
  const { t } = useKiosk();
  const [acked, setAcked] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);

  /*
   * ⚠ 目前只是畫面上的回應：亮三秒的「已通知」，沒有真的送出任何訊息。
   * 原本 StatusBar 的鈴鐺就是這個行為，這裡只是搬過來並補上標示。
   * 要真的叫得到人，得先定義「呼叫」是什麼（現場響鈴／通知 CMS／顯示分機），
   * 那是設計稿標為待確認的項目之一。
   */
  function ringBell(): void {
    setAcked(true);
    window.setTimeout(() => setAcked(false), ACK_MS);
  }

  return (
    <>
      <footer className="k-footer">
        <button type="button" className="k-footer__btn" onClick={ringBell}>
          <img
            className="k-footer__icon"
            src="/brand/footer-support.png"
            alt=""
            aria-hidden="true"
          />
          <span className="k-footer__labels">
            <span className="k-footer__title">{t('footer.support')}</span>
            <span className="k-footer__sub">
              {acked ? t('footer.supportAcked') : t('footer.supportSub')}
            </span>
          </span>
        </button>

        <div className="k-footer__center">
          {step && (
            <div
              className="k-steps"
              role="progressbar"
              aria-valuenow={step.current}
              aria-valuemin={1}
              aria-valuemax={step.total}
              aria-label={t('footer.stepLabel')}
            >
              {Array.from({ length: step.total }, (_, i) => (
                <span
                  key={i}
                  className={`k-steps__dot${i + 1 <= step.current ? ' k-steps__dot--done' : ''}`}
                />
              ))}
            </div>
          )}
        </div>

        <button type="button" className="k-footer__btn" onClick={() => setNoticeOpen(true)}>
          <img
            className="k-footer__icon"
            src="/brand/footer-notice.png"
            alt=""
            aria-hidden="true"
          />
          <span className="k-footer__labels">
            <span className="k-footer__title">{t('footer.notice')}</span>
            <span className="k-footer__sub">{t('footer.noticeSub')}</span>
          </span>
        </button>
      </footer>

      {noticeOpen && <PurchaseNoticeModal onClose={() => setNoticeOpen(false)} />}
    </>
  );
}
